export interface Faq {
  question: string;
  /** Optional so a future addition can still ship collapsed (see faqs.tsx)
   * while real copy is confirmed — but every entry below has one. */
  answer?: string;
}

// Grounded in what Intelligence actually does (see hero.tsx, three-steps.tsx,
// key-stats.tsx, timeline-data.ts) plus real search demand for the
// underlying financial questions it answers, checked via DataForSEO
// (search volume + Google AI Overview/PAA structure) on 2026-09-07:
// - "ltv cac ratio" / "ltv to cac ratio": ~480-320 searches/mo (US), AI
//   Overview benchmark 3:1-5:1.
// - "roas calculator": ~1,300/mo; "what is a good roas" AI Overview gives
//   2:1-4:1 with platform breakdowns (Google 4:1+, Meta 2:1-4:1, TikTok ~1.4:1).
// - "cash flow forecast": ~1,900/mo; "what is the best cash flow forecasting
//   tool?" is a live People Also Ask question.
export const FAQS: Faq[] = [
  {
    question: "What is Wayflyer Intelligence?",
    answer:
      "Intelligence is the AI tool that connects your Shopify, Amazon, Meta, and banking data to Wayflyer's cashflow analysis expertise. Ask it questions in plain English - like how revenue trended last quarter or where cash flow is tightest - and get straight answers instead of building your own dashboards.",
  },
  {
    question: "Is Wayflyer Intelligence free to use?",
    answer:
      "Yes, for Wayflyer customers. Intelligence is currently only available to businesses that have taken financing with Wayflyer - once you have, it's free. It's the kind of analytics enterprise brands normally pay for, included at no extra cost for the growing businesses we back.",
  },
  {
    question: "What is a good LTV:CAC ratio for an eCommerce brand?",
    answer:
      "Most benchmarks put a healthy LTV:CAC ratio at 3:1 or higher - for every $1 you spend acquiring a customer, you want at least $3 back in lifetime value. Below 1:1 means you're losing money on every customer; above 5:1 can mean you're under-investing in growth. Ask Intelligence “Is my LTV:CAC healthy?” to see where your brand actually stands.",
  },
  {
    question: "What's a good ROAS, and does it differ between Meta and Google?",
    answer:
      "A good ROAS typically falls between 2:1 and 4:1, with eCommerce averaging around 2.87:1 - though it depends on your margins. Google Search/Shopping tends to run higher (4:1 or more) since people are already searching to buy, while Meta usually sits in the 2:1-4:1 range for impulse and lifestyle purchases. Ask Intelligence “Meta vs. Google - where should we put our budget?” to see which platform is actually paying off for your spend.",
  },
  {
    question: "How do I forecast cash flow for my eCommerce business?",
    answer:
      "List your expected cash inflows (sales, refunds owed) and outflows (inventory, ad spend, payroll) over the coming weeks or months, then run a rolling total to see where the gaps are. Intelligence does this automatically from your real Shopify, Amazon, and banking data - ask it “Where is cash flow tightest in the months ahead?” and get an answer without building a spreadsheet.",
  },
  {
    question: "What data sources does Wayflyer Intelligence connect to?",
    answer:
      "Shopify, Amazon, Meta, and your business banking, with more platforms being added. Connect your accounts once and Intelligence pulls your real trading data automatically - no manual exports or spreadsheet-building required.",
  },
  {
    question:
      "How is Wayflyer Intelligence different from a regular BI dashboard?",
    answer:
      "Most business intelligence tools still leave you building reports and reading charts. Intelligence skips that step - ask a plain-English question like “How could we scale revenue 10% next year?” and it answers directly, backed by your own data and Wayflyer's experience financing thousands of similar brands.",
  },
];
